import * as React from 'react';
import { useDebounce } from 'react-use';
import { Box, Grid, ToggleButtonGroup, ToggleButton, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import MessageIcon from '@mui/icons-material/Message';
import GridViewIcon from '@mui/icons-material/GridView';
import { EnhancedTable } from 'components';
import { feedbacksTableColumns } from 'configs';
import { convertFeedbacksToTableData } from 'utils';
import { FeedbackFilter, FeedbackSortColumn, FeedbackSortDirection } from 'models';
import { useFeedbacks } from './feedbacks.hook';
import { useAppDispatch } from 'store/store.hook';
import { updateLayout } from 'store/slices/layout.slice';

interface FeedbackPageProps {}

export const FeedbackPage: React.FC<FeedbackPageProps> = () => {
  const dispatch = useAppDispatch();
  const [filter, setFilter] = React.useState<FeedbackFilter>('all');
  const [sort] = React.useState<FeedbackSortColumn>('created_at');
  const [direction] = React.useState<FeedbackSortDirection>('desc');
  const [buildingSearch, setBuildingSearch] = React.useState('');
  const [debouncedBuildingSearch, setDebouncedBuildingSearch] = React.useState('');

  useDebounce(() => setDebouncedBuildingSearch(buildingSearch.trim()), 300, [buildingSearch]);

  const [loading, feedback, counts] = useFeedbacks({
    filter,
    search: debouncedBuildingSearch || undefined,
    sort,
    direction,
  });

  const hasActiveFilters = filter !== 'all' || debouncedBuildingSearch !== '';
  const noDataLabel = hasActiveFilters && counts.all > 0 ? 'No feedback matches your filters' : 'No feedback';

  React.useEffect(() => {
    dispatch(updateLayout({ headerTitle: 'Feedback' }));
  }, []);

  return (
    <>
      <Grid container sx={{ alignItems: 'center', gap: 2, padding: 3, pb: 0 }}>
        <Grid item xs={12}>
          <TextField
            sx={{ width: { xs: '100%', md: '50%' } }}
            size="small"
            placeholder="Search by building name or address"
            value={buildingSearch}
            onChange={(e) => setBuildingSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item>
          <ToggleButtonGroup
            value={filter}
            exclusive
            onChange={(_, value) => value && setFilter(value)}
            size="small"
            color="primary"
          >
            <ToggleButton value="all">
              <GridViewIcon sx={{ mr: 1 }} fontSize="small" />
              All ({counts.all})
            </ToggleButton>

            <ToggleButton value="ratings">
              <StarIcon sx={{ mr: 1 }} fontSize="small" />
              Ratings ({counts.ratings})
            </ToggleButton>

            <ToggleButton value="comments">
              <MessageIcon sx={{ mr: 1 }} fontSize="small" />
              Comments ({counts.comments})
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      </Grid>
      <Box sx={{ padding: 3 }}>
        <EnhancedTable
          config={{ toolbar: false, rowCheckable: false }}
          loading={loading}
          loadingLabel={'Loading feedbacks'}
          noDataLabel={noDataLabel}
          data={feedback}
          convertFunctionForDataToTableData={convertFeedbacksToTableData}
          headColumns={feedbacksTableColumns}
          columnKeys={['date', 'zone', 'zone_name', 'building_name', 'name', 'email', 'phone', 'message', 'rating']}
          styles={{ tableStyle: { minWidth: 0 } }}
          initialRowsPerPage={10}
          visiblePagination
          rowActions={{}}
        />
      </Box>
    </>
  );
};
